import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-2">Terms & Conditions</h1>
          <p className="text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">1. Agreement to Terms</h2>
              <p>By accessing and using DressPay ("the Website"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">2. Description of Services</h2>
              <p>DressPay is an e-commerce platform offering:</p>
              <ul className="list-disc ml-6 space-y-2 mt-2">
                <li>Fruits & Drinks ($1-$20)</li>
                <li>Toys & Baby Clothes ($20-$60)</li>
                <li>Diapers ($60-$70)</li>
                <li>Men & Women Clothes ($70-$130)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">3. Account Registration</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>You must create an account to make purchases</li>
                <li>You are responsible for maintaining account security</li>
                <li>You must provide accurate and current information</li>
                <li>One account per person; sharing accounts is prohibited</li>
                <li>You must be 18+ years old to create an account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">4. Product Information</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>We strive to display accurate product information</li>
                <li>Colors may vary slightly from images</li>
                <li>We reserve the right to discontinue products</li>
                <li>Prices are subject to change without notice</li>
                <li>Product availability is not guaranteed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">5. Pricing & Payment</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>All prices are in USD unless otherwise stated</li>
                <li>Prices include applicable taxes</li>
                <li>Payment is processed securely through Stripe</li>
                <li>We accept Credit/Debit Cards, Apple Pay, Cash App, and Cryptocurrency</li>
                <li>Payment must be received before order processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">6. Orders & Shipping</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>Order confirmation is sent via email</li>
                <li>Shipping times vary by location</li>
                <li>We are not responsible for shipping delays</li>
                <li>Risk of loss passes to customer upon delivery</li>
                <li>Incorrect addresses may result in non-delivery</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">7. Returns & Refunds</h2>
              <p>Please refer to our separate policies:</p>
              <ul className="list-disc ml-6 space-y-2 mt-2">
                <li><Link to="/refund-policy" className="text-indigo-600 hover:underline">Refund Policy</Link></li>
                <li><Link to="/return-policy" className="text-indigo-600 hover:underline">Return Policy</Link></li>
                <li><Link to="/cancellation-policy" className="text-indigo-600 hover:underline">Cancellation Policy</Link></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">8. Prohibited Activities</h2>
              <p className="mb-2">You may NOT:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Use the site for illegal purposes</li>
                <li>Attempt to hack or compromise security</li>
                <li>Create fake accounts or impersonate others</li>
                <li>Resell products commercially without permission</li>
                <li>Upload malicious code or viruses</li>
                <li>Spam or harass other users or staff</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">9. Intellectual Property</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li>All content is owned by DressPay or licensors</li>
                <li>You may not copy, modify, or distribute our content</li>
                <li>Product images and descriptions are copyrighted</li>
                <li>DressPay logo and branding are protected trademarks</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">10. Limitation of Liability</h2>
              <p>DressPay is not liable for:</p>
              <ul className="list-disc ml-6 space-y-2 mt-2">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits or data</li>
                <li>Service interruptions or errors</li>
                <li>Third-party actions (shipping carriers, payment processors)</li>
                <li>Product misuse or accidents</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">11. Governing Law</h2>
              <p>These terms are governed by the laws of the State of New York, United States. Any disputes will be resolved in New York courts.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">12. Changes to Terms</h2>
              <p>We reserve the right to modify these terms at any time. Changes take effect immediately upon posting. Continued use of the site constitutes acceptance of modified terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">13. Contact Us</h2>
              <ul className="list-none ml-0 space-y-1">
                <li><strong>Email:</strong> <a href="mailto:support@dresspay.com" className="text-indigo-600 hover:underline">support@dresspay.com</a></li>
                <li><strong>Phone:</strong> <a href="tel:3464837337" className="text-indigo-600 hover:underline">346-483-7337</a></li>
                <li><strong>Address:</strong> Online Store - Nationwide Shipping</li>
                <li><strong>Hours:</strong> Monday - Friday, 9 AM - 6 PM EST</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
